import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { getCommon } from '../../API/query/common';
import userManager from '../../API/userManager';
import { handleMapToOptions } from '../functions/handleMapToOptions';
import { sortAlphabetically } from '../functions/sortAlphabetically';

const COMMON_CACHE_TIME = 1000 * 60 * 30;

export const useCommonData = () => {
	const { data, refetch, isLoading, isFetching } = useQuery(
		['common'],
		() => {
			return userManager.getUser()
				? getCommon()
				: () => ({ users: [], markets: [], products: [], queues: [] });
		},
		{
			onSuccess: (successData) => {
				handleSetOptions(successData);
				userManager.setCommonData(successData);
			},
			initialData: {
				users: userManager.getCommonData()?.users || [],
				markets: userManager.getCommonData()?.markets || [],
				products: userManager.getCommonData()?.products || [],
				queues: userManager.getCommonData()?.queues || [],
				usersOptions: userManager.getCommonData()?.usersOptions || [],
				productsOptions:
					sortAlphabetically(
						userManager.getCommonData()?.productsOptions,
						'label',
					) || [],
				marketsOptions:
					sortAlphabetically(
						userManager.getCommonData()?.marketsOptions,
						'label',
					) || [],
				queuesOptions: userManager.getCommonData()?.queuesOptions || [],
				COUNTRIES: userManager.getCommonData()?.COUNTRIES || [],
			},
			refetchInterval: COMMON_CACHE_TIME,
			refetchOnWindowFocus: false,
		},
	);

	const {
		users,
		products,
		markets,
		queues,
		usersOptions,
		productsOptions,
		marketsOptions,
		queuesOptions,
		COUNTRIES,
	} = data;

	const options = {
		usersOptions,
		marketsOptions,
		productsOptions,
		queuesOptions,
	};
	const commonData = { users, markets, queues, products };
	const queryFunctions = { refetch };
	const queryValues = { isFetching, isLoading };

	const handleSetOptions = (successData) => {
		successData.usersOptions = handleMapToOptions(
			successData.users,
			'username',
			'_id',
		);
		successData.marketsOptions = sortAlphabetically(
			handleMapToOptions(successData.markets, 'name', '_id'),
			'label',
		);
		successData.productsOptions = sortAlphabetically(
			handleMapToOptions(successData.products, 'name', '_id'),
			'label',
		);
		successData.queuesOptions = handleMapToOptions(
			successData.queues?.sort((qA, qB) => qB.select_weight - qA.select_weight),
			'name',
		);
		successData.COUNTRIES = handleMapToOptions(
			successData.markets,
			'name',
			'short',
		);
	};

	const filterUsersByRoles = useCallback(
		(roles, onlyActive = false) => {
			return handleMapToOptions(
				users.filter((user) =>
					onlyActive
						? roles.includes(user.role) && user.active
						: roles.includes(user.role),
				),
				'username',
				'_id',
			);
		},
		[users],
	);

	const getActiveUsersOptions = useCallback(() => {
		return handleMapToOptions(
			users.filter((user) => user.active),
			'username',
			'_id',
		);
	}, [users]);

	return {
		commonData,
		options,
		filterUsersByRoles,
		getActiveUsersOptions,
		queryValues,
		queryFunctions,
		COUNTRIES,
	};
};
